import React, { useEffect, useMemo, useState } from "react";
import { Boxes, CalendarClock, Database } from "lucide-react";
import { Button } from "./ui/button";
import { Carousel, CarouselContent, CarouselItem } from "./ui/carousel";
import { BimInfographic } from "./BimInfographic";
import { getApiBase } from "../lib/apiBase";
import { useCarouselAutoplay } from "../hooks/use-carousel-autoplay";

const FLOATING_BADGES = [
  { label: "BIM Data", icon: Database, position: "absolute -top-3 right-8 sm:right-16", testId: "home-hero-badge-data" },
  { label: "Maquette numérique", icon: Boxes, position: "absolute -bottom-3 left-10 sm:left-20", testId: "home-hero-badge-mockup" },
  { label: "Planning 4D", icon: CalendarClock, position: "absolute -bottom-3 right-10 sm:right-24", testId: "home-hero-badge-planning" },
];

const FloatingBadge = ({ badge }) => {
  const Icon = badge.icon;
  return (
    <div
      aria-hidden="true"
      className={`${badge.position} z-20 inline-flex items-center gap-2.5 rounded-full border border-[#cfe9e2] bg-white/92 px-5 py-2.5 text-[15px] font-semibold text-[#0f766e] shadow-[0_18px_36px_-16px_rgba(15,118,110,0.4)] backdrop-blur-md`}
      data-testid={badge.testId}
    >
      <Icon className="h-5 w-5 text-[#0f766e]" strokeWidth={2.2} />
      {badge.label}
    </div>
  );
};

const HomeHeroMediaSlide = ({ item }) => (
  <div className="relative mx-auto w-[94vw] max-w-[1850px] lg:w-[92vw]" data-testid={`home-hero-media-slide-${item.id}`}>
    {/* Ambient halo behind the card */}
    <div aria-hidden="true" className="pointer-events-none absolute -inset-x-8 -inset-y-10 -z-10 bg-[radial-gradient(60%_55%_at_50%_50%,_rgba(45,212,191,0.18),_transparent_70%),radial-gradient(40%_40%_at_15%_30%,_rgba(190,242,100,0.18),_transparent_70%),radial-gradient(40%_40%_at_85%_70%,_rgba(94,234,212,0.16),_transparent_70%)] blur-2xl" />
    {/* Premium frame */}
    <div className="relative overflow-hidden rounded-[2.9rem] border border-[#d9e7d5] bg-[#fbfdf8] p-3 shadow-[0_46px_140px_-70px_rgba(16,185,129,0.42)] ring-1 ring-inset ring-white/70 sm:p-4 lg:p-5">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2.9rem] bg-[radial-gradient(circle_at_top,_rgba(190,242,100,0.18),_transparent_28%),radial-gradient(circle_at_right,_rgba(94,234,212,0.16),_transparent_30%),linear-gradient(180deg,_#fcfef9_0%,_#f8fbf4_100%)]" />
      {/* Subtle dot-grid texture */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[2.9rem] opacity-[0.18] [background-image:radial-gradient(rgba(15,118,110,0.45)_1px,transparent_1px)] [background-size:22px_22px]" />
      {/* Floating brand badge */}
      <div aria-hidden="true" className="absolute left-6 top-6 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/80 bg-white/75 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#0f766e] shadow-[0_8px_20px_-12px_rgba(15,118,110,0.35)] backdrop-blur">
        <span className="h-1.5 w-1.5 rounded-full bg-[#0f766e]" />
        Bimcubes · Aperçu
      </div>
      <div className="relative aspect-[1672/941] w-full overflow-hidden rounded-[2rem] ring-1 ring-[#d9e7d5]/70">
        <img alt={item.filename} className="h-full w-full object-contain" data-testid={`home-hero-media-image-${item.id}`} src={`${getApiBase()}${item.url}`} />
        {/* Green / cyan tint overlay — subtle but perceptible */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-multiply bg-[linear-gradient(135deg,_rgba(16,185,129,0.10)_0%,_rgba(94,234,212,0.06)_50%,_rgba(45,212,191,0.10)_100%)]" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_50%,_transparent_55%,_rgba(15,118,110,0.10)_100%)]" />
      </div>
      {/* Floating BIM badges */}
      {FLOATING_BADGES.map((badge) => (
        <FloatingBadge badge={badge} key={badge.label} />
      ))}
    </div>
  </div>
);

export const HomeHeroCarousel = () => {
  const [carouselApi, setCarouselApi] = useState();
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);

  useEffect(() => {
    let active = true;
    const API = getApiBase();
    fetch(`${API}/api/home/hero-media`)
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (!active) return;
        setMediaItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setMediaItems([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!carouselApi) return undefined;
    const onSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on("select", onSelect);
    return () => carouselApi.off("select", onSelect);
  }, [carouselApi]);

  const slides = useMemo(() => [{ id: "fixed-home-hero", type: "fixed" }, ...mediaItems.map((item) => ({ ...item, type: "media" }))], [mediaItems]);
  const { handleMouseEnter, handleMouseLeave, handleManualNavigation } = useCarouselAutoplay(carouselApi, slides.length);

  return (
    <div className="w-full" data-testid="home-hero-carousel-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Carousel className="w-full" opts={{ loop: slides.length > 1 }} setApi={setCarouselApi}>
        <CarouselContent className="-ml-0">
          {slides.map((slide) => (
            <CarouselItem className="pl-0" key={slide.id}>
              {slide.type === "fixed" ? <BimInfographic /> : <HomeHeroMediaSlide item={slide} />}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {slides.length > 1 ? (
        <div className="mt-6 flex items-center justify-between gap-4" data-testid="home-hero-carousel-navigation">
          <div className="flex items-center gap-2" data-testid="home-hero-carousel-dots">
            {slides.map((slide, index) => (
              <button
                className={`h-2.5 rounded-full transition-all ${activeIndex === index ? "w-10 bg-[#0f766e]" : "w-2.5 bg-[#0f766e]/25 hover:bg-[#0f766e]/45"}`}
                data-testid={`home-hero-carousel-dot-${index + 1}`}
                key={slide.id}
                onClick={() => handleManualNavigation(() => carouselApi?.scrollTo(index))}
                type="button"
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="home-hero-carousel-prev-button" onClick={() => handleManualNavigation(() => carouselApi?.scrollPrev())} type="button" variant="outline">
              Précédent
            </Button>
            <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="home-hero-carousel-next-button" onClick={() => handleManualNavigation(() => carouselApi?.scrollNext())} type="button" variant="outline">
              Suivant
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};